import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, SignIn, SignUp } from '@clerk/clerk-react';
import { Users, AlertCircle, Building2 } from 'lucide-react';
import { getStoredInviteToken, clearInviteToken } from '../utils/inviteTokenHandler';
import MarketingLayout from '../components/MarketingLayout';
import { authApi } from '../services/api';

interface InvitationData {
  token: string;
  organizationName?: string;
  inviterName?: string;
  role?: string;
  companyId?: string;
}

interface InvitationStatus {
  status: 'loading' | 'ready' | 'processing' | 'success' | 'error' | 'redirecting';
  message?: string;
}

export default function InvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [invitationStatus, setInvitationStatus] = useState<InvitationStatus>({ status: 'loading' });

  // Default to signup if Clerk indicates that's the intended flow
  const defaultAuthMode = searchParams.get('__clerk_status') === 'sign_up' ? 'signup' : 'signin';
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(defaultAuthMode);

  // Extract invitation data from URL or storage
  useEffect(() => {
    // Check if Clerk is handling the auth flow (has Clerk-specific parameters)
    const isClerkAuthFlow =
      searchParams.get('__clerk_status') || searchParams.get('__clerk_ticket');

    if (isClerkAuthFlow) {
      console.log('Clerk is handling auth flow, extracting invitation details from ticket...');
      console.log('URL parameters:', Object.fromEntries(searchParams.entries()));

      // Extract organization details from the Clerk ticket if possible
      const ticket = searchParams.get('__clerk_ticket');
      let organizationName =
        searchParams.get('organization') || searchParams.get('org_name') || 'the organization';
      let inviterName = searchParams.get('inviter_name') || searchParams.get('inviter');
      let role = searchParams.get('role');
      let companyId = searchParams.get('company_id');

      if (ticket) {
        try {
          // Decode the JWT token to extract the redirect URL
          const payload = JSON.parse(atob(ticket.split('.')[1]));
          const redirectUrl = payload.rurl;

          if (redirectUrl) {
            const url = new URL(redirectUrl);
            organizationName = decodeURIComponent(
              url.searchParams.get('organization') || organizationName
            );
            inviterName = decodeURIComponent(
              url.searchParams.get('inviter_name') || inviterName || ''
            );
            role = url.searchParams.get('role') || role;
            companyId = url.searchParams.get('company_id') || companyId;
          }
        } catch (error) {
          console.log('Could not decode Clerk ticket:', error);
        }
      }

      // Additional fallback: try to get organization name from URL parameters
      if (organizationName === 'the organization') {
        organizationName =
          searchParams.get('organization') || searchParams.get('org_name') || 'the organization';
      }

      // One more fallback: try to get organization name from current URL
      if (organizationName === 'the organization') {
        const currentUrl = new URL(window.location.href);
        organizationName =
          currentUrl.searchParams.get('organization') ||
          currentUrl.searchParams.get('org_name') ||
          'the organization';
      }

      console.log('Final organization name extracted:', organizationName);

      const invitationData: InvitationData = {
        token: 'clerk_auth_flow',
        organizationName,
        inviterName: inviterName || undefined,
        role: role || undefined,
        companyId: companyId || undefined,
      };

      setInvitation(invitationData);
      setInvitationStatus({ status: 'ready' });

      // Store for later processing
      sessionStorage.setItem(
        'clerk_invite_token',
        JSON.stringify({
          token: 'clerk_auth_flow',
          capturedAt: Date.now(),
          organization: organizationName,
          role: role,
          companyId: companyId,
        })
      );

      // Try to get organization name from existing session storage as well
      const existingStoredData = sessionStorage.getItem('clerk_invite_token');
      if (existingStoredData && organizationName === 'the organization') {
        try {
          const parsed = JSON.parse(existingStoredData);
          if (parsed.organization && parsed.organization !== 'the organization') {
            organizationName = parsed.organization;
          }
        } catch (e) {
          // Silent fallback
        }
      }

      return;
    }

    // Handle regular invitation flow
    let token =
      searchParams.get('invite_token') ||
      searchParams.get('invitation_token') ||
      searchParams.get('token') ||
      getStoredInviteToken()?.token;

    // Check if we have organization details without a token (Clerk redirectUrl case)
    const hasOrgDetails = searchParams.get('organization') || searchParams.get('company_id');

    if (!token && hasOrgDetails) {
      // This is likely a Clerk redirectUrl invitation without explicit token
      // Create a placeholder token to proceed with the flow
      token = 'clerk_redirect_invitation';
      console.log('Using Clerk redirect invitation flow');
    }

    if (!token) {
      setInvitationStatus({
        status: 'error',
        message: 'No invitation found. Please check your invitation link or email.',
      });
      return;
    }

    // Extract additional invitation details from URL
    const invitationData: InvitationData = {
      token,
      organizationName:
        searchParams.get('organization') || searchParams.get('org_name') || undefined,
      inviterName: searchParams.get('inviter_name') || searchParams.get('inviter') || undefined,
      role: searchParams.get('role') || undefined,
      companyId: searchParams.get('company_id') || undefined,
    };

    setInvitation(invitationData);
    setInvitationStatus({ status: 'ready' });

    // Store the token for later processing
    if (!getStoredInviteToken()) {
      sessionStorage.setItem(
        'clerk_invite_token',
        JSON.stringify({
          token: invitationData.token,
          capturedAt: Date.now(),
          organization: invitationData.organizationName,
          role: invitationData.role,
          companyId: searchParams.get('company_id') || undefined,
        })
      );
    }

    // Clean the URL (but preserve company_id for processing)
    const url = new URL(window.location.href);
    const _companyId = url.searchParams.get('company_id');

    // Remove invite-related parameters but keep company_id
    url.searchParams.delete('invite_token');
    url.searchParams.delete('invitation_token');
    url.searchParams.delete('token');
    url.searchParams.delete('organization');
    url.searchParams.delete('inviter_name');
    url.searchParams.delete('role');

    window.history.replaceState({}, '', url.toString());
  }, [searchParams]);

  // Redirect user after authentication (webhooks handle invitation processing automatically)
  useEffect(() => {
    if (isSignedIn && isLoaded) {
      // Show redirecting status to prevent any error messages
      setInvitationStatus({
        status: 'redirecting',
        message: `Welcome aboard! Redirecting you to your dashboard...`,
      });

      // Clear any stored tokens since webhooks handle everything
      clearInviteToken();

      const storedToken = getStoredInviteToken();
      const companyId = searchParams.get('company_id') || storedToken?.companyId;

      // Actively sync memberships from Clerk to ensure DB is up to date
      const syncAndRedirect = async () => {
        try {
          console.log('Syncing memberships after invitation acceptance...');
          const _syncResponse = await authApi.syncMemberships();
          console.log('Membership sync completed successfully');
        } catch (syncError: any) {
          console.error('Error syncing memberships (non-critical):', syncError);
          // If rate limited or any error, just continue - webhooks may have handled it
          // The dashboard has its own fallback sync mechanism
          if (syncError?.status === 429) {
            console.warn('Rate limit hit during invitation sync, continuing to dashboard');
          }
        }

        // Small delay to ensure any remaining webhook processing completes
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Navigate to the company or dashboard
        if (companyId) {
          navigate(`/company/${companyId}`);
        } else {
          navigate('/dashboard');
        }
      };

      syncAndRedirect();
    }
  }, [isSignedIn, isLoaded, navigate, searchParams]);

  const authAppearance = {
    elements: {
      rootBox: 'w-full',
      card: 'bg-white rounded-2xl border border-blue-100 shadow-sm w-full',
      headerTitle: 'text-2xl font-bold text-gray-900',
      headerSubtitle: 'text-gray-600',
      formButtonPrimary:
        'bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold text-white',
      footerActionLink: 'text-blue-600 font-medium',
      formFieldInput: 'rounded-lg border-gray-200 focus:border-blue-500',
      formFieldLabel: 'text-gray-700 font-medium',
    },
  };

  if (invitationStatus.status === 'loading' || invitationStatus.status === 'redirecting') {
    const redirecting = invitationStatus.status === 'redirecting';
    return (
      <MarketingLayout>
        <section className="public-section">
          <div className="public-container">
            <div
              className="public-card public-card--white p-8 max-w-lg mx-auto text-center"
              role="status"
              aria-live="polite"
            >
              <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-6" />
              <p className="public-eyebrow">Your team, connected</p>
              <h1 className="text-3xl font-bold mb-4">
                {redirecting ? 'Opening your team’s workspace.' : 'Getting your invitation ready.'}
              </h1>
              <p className="text-gray-600">
                {redirecting
                  ? 'Your sign-in is complete. We’re connecting your membership and taking you to your workspace.'
                  : 'We’re checking the details so you can join the people behind the plan.'}
              </p>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  if (invitationStatus.status === 'error') {
    return (
      <MarketingLayout>
        <section className="public-section">
          <div className="public-container">
            <div className="public-card public-card--white p-8 max-w-xl mx-auto text-center">
              <AlertCircle className="h-10 w-10 text-blue-600 mx-auto mb-5" />
              <p className="public-eyebrow">Let’s find your way into the team</p>
              <h1 className="text-3xl font-bold mb-4">We couldn’t open this invitation.</h1>
              <p className="text-gray-600 mb-7" role="alert">
                {invitationStatus.message ||
                  'Check the link in your invitation email, refresh this page or open your dashboard to see whether your membership is already available.'}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="public-button public-button--primary"
                >
                  Open my dashboard
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="public-button public-button--secondary"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section className="public-section">
        <div className="public-container">
          {!isSignedIn ? (
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start max-w-5xl mx-auto">
              <div className="public-auth-intro">
                <p className="public-eyebrow">A shared direction. Your next contribution.</p>
                <h1 className="text-4xl md:text-5xl mb-5">Your team’s next chapter starts here.</h1>
                <p className="text-lg text-gray-600 mb-7">
                  {invitation?.inviterName
                    ? `${invitation.inviterName} has invited you to join`
                    : 'You’ve been invited to join'}{' '}
                  {invitation?.organizationName || 'your team'} in StratPlan. Connect to the
                  priorities, people and work behind the plan.
                </p>
                <div className="public-card p-6 mb-6 flex gap-4 items-center">
                  <Building2 className="h-8 w-8 text-blue-600 shrink-0" />
                  <div>
                    <p className="font-bold text-lg">
                      {invitation?.organizationName || 'Your organization'}
                    </p>
                    {invitation?.role && (
                      <p className="text-sm text-gray-600">
                        Your role: <span className="capitalize">{invitation.role}</span>
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-gray-600 mb-5">
                  Sign in to your existing account or create an account to continue with this
                  invitation.
                </p>
                <div className="flex flex-wrap gap-3" aria-label="Choose how to join">
                  <button
                    type="button"
                    onClick={() => setAuthMode('signin')}
                    aria-pressed={authMode === 'signin'}
                    className={`public-button ${authMode === 'signin' ? 'public-button--primary' : 'public-button--secondary'}`}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMode('signup')}
                    aria-pressed={authMode === 'signup'}
                    className={`public-button ${authMode === 'signup' ? 'public-button--primary' : 'public-button--secondary'}`}
                  >
                    Create an account
                  </button>
                </div>
              </div>
              <div className="public-auth-panel">
                {authMode === 'signup' ? (
                  <SignUp
                    routing="hash"
                    fallbackRedirectUrl="/invitation"
                    signInUrl="/invitation"
                    signInFallbackRedirectUrl="/invitation"
                    appearance={authAppearance}
                  />
                ) : (
                  <SignIn
                    routing="hash"
                    fallbackRedirectUrl="/invitation"
                    signUpUrl="/invitation"
                    signUpFallbackRedirectUrl="/invitation"
                    appearance={authAppearance}
                  />
                )}
              </div>
            </div>
          ) : (
            <div
              className="public-card public-card--white p-8 max-w-lg mx-auto text-center"
              role="status"
            >
              <Users className="h-10 w-10 text-blue-600 mx-auto mb-5" />
              <h1 className="text-3xl mb-4">Your next contribution is ahead.</h1>
              <p className="text-gray-600">
                We’re opening your workspace for {invitation?.organizationName || 'the team'}.
              </p>
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
